import { CheckIcon, ChevronRightIcon } from "@heroicons/react/outline";
import React from "react";
import { cn } from "ui/lib/utils";
import { BottomSheet } from "./bottom-sheet";

export type ActionMenuOption = {
  value: string;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  /** Destructive: shown apart, last, in red. Confirm it in the handler. */
  dangerous?: boolean;
  /** Shows a check, for a picker's current choices. */
  selected?: boolean;
  /** Options sharing a group are listed together under its name in the sheet. */
  group?: string;
};

type ActionMenuProps = {
  /** The sheet's title, e.g. the item's name. */
  title: React.ReactNode;
  options: ActionMenuOption[];
  onSelect: (value: string) => void;
  /** A single element; it receives the open handler (and Radix's ref). */
  trigger: React.ReactElement<{ onClick?: (event: React.MouseEvent) => void }>;
  /** Controlled open state, e.g. for opening from a long-press. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Keep the sheet open after a choice, for multi-select pickers. */
  closeOnSelect?: boolean;
  /** Groups listed behind one row that expands in place, e.g. "Share & export". */
  collapsedGroups?: { name: string; icon?: React.ReactNode }[];
  /**
   * A search field over the options once there are more than `minOptions`
   * (12 by default). Matches the label unless `match` says otherwise.
   */
  search?: {
    placeholder: string;
    minOptions?: number;
    match?: (option: ActionMenuOption, query: string) => boolean;
  };
};

const matchesLabel = (option: ActionMenuOption, query: string) =>
  option.label.toLowerCase().includes(query.toLowerCase());

/**
 * A list of actions as a bottom sheet: the mobile shell's menu. Callers
 * render it below lg (`useCompactShell`) and their dropdown on desktop.
 */
export const ActionMenu = ({
  title,
  options,
  onSelect,
  trigger,
  open: openProp,
  onOpenChange,
  closeOnSelect = true,
  collapsedGroups = [],
  search,
}: ActionMenuProps) => {
  const [openState, setOpenState] = React.useState(false);
  const [expanded, setExpanded] = React.useState<string[]>([]);
  const [query, setQuery] = React.useState("");
  const open = openProp ?? openState;
  const setOpen = (next: boolean) => {
    if (next) {
      setExpanded([]);
      setQuery("");
    }
    setOpenState(next);
    onOpenChange?.(next);
  };
  if (process.env.NODE_ENV !== "production") {
    const withIcons = options.filter((o) => o.icon).length;
    if (withIcons && withIcons !== options.length) {
      console.error("ActionMenu: icons on some rows but not all", title);
    }
  }
  const collapsed = (name: string) =>
    collapsedGroups.some((group) => group.name === name);
  const collapsedIcon = (name: string) =>
    collapsedGroups.find((group) => group.name === name)?.icon;
  const searching = !!search && options.length > (search.minOptions ?? 12);
  const trimmed = query.trim();
  const shown =
    searching && trimmed
      ? options.filter((o) => (search.match ?? matchesLabel)(o, trimmed))
      : options;
  const safe = shown.filter((o) => !o.dangerous);
  const dangerous = shown.filter((o) => o.dangerous);

  const choose = (value: string) => {
    if (closeOnSelect) setOpen(false);
    onSelect(value);
  };

  const row = (o: ActionMenuOption) => (
    <button
      key={o.value}
      type="button"
      disabled={o.disabled}
      aria-pressed={o.selected ?? undefined}
      onClick={() => choose(o.value)}
      className={cn(
        "flex min-h-[52px] w-full items-center gap-3 rounded-xl px-3 text-left text-[1rem] transition-colors active:bg-white/[0.06] disabled:opacity-40",
        o.dangerous ? "text-error" : "text-white/90"
      )}
    >
      {o.icon ? <span className="shrink-0 text-faint">{o.icon}</span> : null}
      <span className="min-w-0 flex-1">{o.label}</span>
      {o.selected ? (
        <CheckIcon
          aria-hidden="true"
          className="size-5 shrink-0 text-primary"
        />
      ) : null}
    </button>
  );
  const groups = safe.reduce<{ name?: string; options: ActionMenuOption[] }[]>(
    (list, o) => {
      const last = list[list.length - 1];
      if (last && last.name === o.group) last.options.push(o);
      else list.push({ name: o.group, options: [o] });
      return list;
    },
    []
  );
  return (
    <>
      {React.cloneElement(trigger, {
        onClick: (event: React.MouseEvent) => {
          event.stopPropagation();
          trigger.props.onClick?.(event);
          setOpen(true);
        },
      })}
      <BottomSheet
        open={open}
        onOpenChange={setOpen}
        title={title}
        full={searching}
      >
        {searching ? (
          <div className="pb-3">
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={search.placeholder}
              aria-label={search.placeholder}
              className="h-11 w-full rounded-xl bg-white/[0.06] px-4 text-[16px] text-white/90 placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            {!shown.length ? (
              <p className="px-3 pt-3 text-[15px] text-faint">No matches</p>
            ) : null}
          </div>
        ) : null}
        <div className="space-y-3">
          {groups.map((g, index) => (
            <div key={g.name ?? index} role="group" aria-label={g.name}>
              {g.name && !(collapsed(g.name) && !expanded.includes(g.name)) ? (
                <p className="px-3 pb-1 text-[0.75rem] font-medium uppercase tracking-wider text-faint">
                  {g.name}
                </p>
              ) : null}
              {g.name && collapsed(g.name) && !expanded.includes(g.name) ? (
                <button
                  type="button"
                  aria-expanded={false}
                  onClick={() => setExpanded((list) => [...list, g.name])}
                  className="flex min-h-[52px] w-full items-center gap-3 rounded-xl px-3 text-left text-[1rem] text-white/90 transition-colors active:bg-white/[0.06]"
                >
                  {collapsedIcon(g.name) ? (
                    <span className="shrink-0 text-faint">
                      {collapsedIcon(g.name)}
                    </span>
                  ) : null}
                  <span className="min-w-0 flex-1">{g.name}</span>
                  <span className="text-[0.9375rem] tabular-nums text-faint">
                    {g.options.length}
                  </span>
                  <ChevronRightIcon
                    aria-hidden="true"
                    className="size-5 shrink-0 text-faint"
                  />
                </button>
              ) : (
                <div className="space-y-0.5">{g.options.map(row)}</div>
              )}
            </div>
          ))}
        </div>
        {dangerous.length ? (
          <div className="mt-2 space-y-0.5 border-t border-white/[0.06] pt-2">
            {dangerous.map(row)}
          </div>
        ) : null}
      </BottomSheet>
    </>
  );
};
